// Encrypt and decrypt using AES-256-CBC in the browser

const iv = new Uint8Array(16); // 16-byte IV filled with 0s (static for simplicity, insecure in practice)

// Utility to encode and decode
const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

// Hash the password using SHA-256 to create a 256-bit key
async function getKey(password: string): Promise<CryptoKey> {
  const passwordBuffer = textEncoder.encode(password);
  const hashBuffer = await crypto.subtle.digest("SHA-256", passwordBuffer);
  return crypto.subtle.importKey(
    "raw",
    hashBuffer,
    { name: "AES-CBC" },
    false,
    ["encrypt", "decrypt"]
  );
}

// Encrypt a UTF-8 string and return hex
export async function encrypt(text: string, password: string): Promise<string> {
  const key = await getKey(password);
  const encryptedBuffer = await crypto.subtle.encrypt(
    { name: "AES-CBC", iv },
    key,
    textEncoder.encode(text)
  );
  return bufferToHex(encryptedBuffer);
}

// Decrypt a hex string and return UTF-8 string
export async function decrypt(
  encryptedHex: string,
  password: string
): Promise<string> {
  try {
    const key = await getKey(password);
    const encryptedBuffer = hexToBuffer(encryptedHex);
    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: "AES-CBC", iv },
      key,
      encryptedBuffer
    );
    return textDecoder.decode(decryptedBuffer);
  } catch (e) {
    return "N/A";
  }
}

// Helpers to convert between hex and ArrayBuffer
function bufferToHex(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  return [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function hexToBuffer(hex: string): ArrayBuffer {
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    bytes[i / 2] = parseInt(hex.slice(i, i + 2), 16);
  }
  return bytes.buffer;
}
